import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../config/api';
import { MicronutrientMatrix } from '../components/dashboard/MicronutrientMatrix';
import { AllTimeMicronutrientMatrix } from '../components/dashboard/AllTimeMicronutrientMatrix';
import { NutrientTrendsChart } from '../components/dashboard/NutrientTrendsChart';
import { FoodSourceBreakdown } from '../components/nutrition/FoodSourceBreakdown';

interface NutritionOverviewPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

export const NutritionOverviewPage: React.FC<NutritionOverviewPageProps> = ({ user, coachMode, clientId }) => {
  const [selectedReport, setSelectedReport] = useState<string>('micronutrients');
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('14');

  // Helper function to get local date string from meal data (avoid timezone shifting)
  const getMealDateString = (meal: any): string => {
    // If meal_date exists, extract just the date part without timezone conversion
    // meal_date comes as "2025-10-29T00:00:00.000Z" - just take the date part
    if (meal.meal_date) {
      return meal.meal_date.split('T')[0];
    }
    // Otherwise parse timestamp in local timezone
    const date = new Date(meal.timestamp || meal.created_at);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get today's date string
  const getTodayDateString = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch micronutrient data - use direct API for consistency with dashboard
  const { data: micronutrientData, isLoading: micronutrientLoading } = useQuery({
    queryKey: coachMode ? ['client-micronutrients', clientId, selectedTimeframe] : ['micronutrients', user?.id, selectedTimeframe],
    queryFn: async () => {
      // Calculate days to fetch based on selection
      // For "Today" (0), fetch 1 day and include today
      // For other periods, add 1 to fetch extra day but exclude today
      const isToday = selectedTimeframe === '0';
      const daysToFetch = selectedTimeframe === 'all' ? 365 : parseInt(selectedTimeframe) + 1;
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?days=${daysToFetch}${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const allMeals = data.portfolio || [];

      // Filter logic based on timeframe
      const today = getTodayDateString();

      let mealsExcludingToday;
      if (isToday) {
        // "Today" - show ONLY today's meals
        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate === today;
        });
      } else if (selectedTimeframe === '1') {
        // "Yesterday" - show ONLY yesterday's meals
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const year = yesterday.getFullYear();
        const month = String(yesterday.getMonth() + 1).padStart(2, '0');
        const day = String(yesterday.getDate()).padStart(2, '0');
        const yesterdayStr = `${year}-${month}-${day}`;

        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate === yesterdayStr;
        });
      } else if (selectedTimeframe === 'all') {
        // All-time - exclude today only
        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate !== today;
        });
      } else {
        // Other timeframes (3, 7, 14, 30 days) - filter by date range
        const daysBack = parseInt(selectedTimeframe);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysBack);
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        const startDateStr = `${year}-${month}-${day}`;

        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          // Include meals from startDate up to (but not including) today
          return mealDate >= startDateStr && mealDate !== today;
        });
      }

      // Group meals by date to count actual days with meals (exclude zero days)
      const mealsByDate = new Map<string, any[]>();
      const allDates: string[] = [];

      mealsExcludingToday.forEach((meal: any, index: number) => {
        const mealDate = getMealDateString(meal);
        allDates.push(mealDate);

        if (!mealsByDate.has(mealDate)) {
          mealsByDate.set(mealDate, []);
        }
        mealsByDate.get(mealDate)!.push(meal);
      });

      // Debug: show all unique dates found
      const uniqueDates = Array.from(new Set(allDates)).sort();
      console.log(`📅 ${selectedTimeframe}-day timeframe - Found dates:`, {
        totalMeals: mealsExcludingToday.length,
        uniqueDates: uniqueDates,
        dateBreakdown: uniqueDates.map(date => ({
          date,
          mealCount: allDates.filter(d => d === date).length
        }))
      });

      // Smart day filtering: Exclude incomplete days (< 2 meals AND < 800 calories)
      const qualityDays = Array.from(mealsByDate.entries()).filter(([date, dayMeals]) => {
        const mealCount = dayMeals.length;
        const totalCalories = dayMeals.reduce((sum, meal) => sum + (meal.totals?.calories || 0), 0);

        // Keep day if it has 2+ meals OR 800+ calories (catches OMAD and good logging)
        return mealCount >= 2 || totalCalories >= 800;
      });

      const actualDaysWithMeals = qualityDays.length;
      const totalDaysWithData = mealsByDate.size;
      const excludedDays = totalDaysWithData - actualDaysWithMeals;

      console.log(`🔍 ${selectedTimeframe}-day analysis: ${mealsExcludingToday.length} meals across ${totalDaysWithData} days (${actualDaysWithMeals} quality days, ${excludedDays} incomplete days excluded)`);

      // Calculate total nutrition across all meals
      const nutritionTotals = mealsExcludingToday.reduce((totals: any, meal: any) => {
        // Process macros from meal.totals
        if (meal.totals) {
          totals.calories += meal.totals.calories || 0;
          totals.protein += meal.totals.protein || 0;
          totals.carbs += meal.totals.carbs || 0;
          totals.fat += meal.totals.fat || 0;
          totals.fiber += meal.totals.fiber || 0;
        }

        // TIER 1: Use meal-level micronutrient totals if available (photo meals)
        const hasVitamins = meal.vitamins && Object.keys(meal.vitamins).length > 0;
        const hasMinerals = meal.minerals && Object.keys(meal.minerals).length > 0;
        const hasAminoAcids = meal.amino_acids && Object.keys(meal.amino_acids).length > 0;
        const hasFattyAcids = meal.fatty_acids && Object.keys(meal.fatty_acids).length > 0;

        if (hasVitamins) {
          Object.keys(meal.vitamins).forEach(vitamin => {
            totals[vitamin] = (totals[vitamin] || 0) + (parseFloat(meal.vitamins[vitamin]) || 0);
          });
        }
        if (hasMinerals) {
          Object.keys(meal.minerals).forEach(mineral => {
            totals[mineral] = (totals[mineral] || 0) + (parseFloat(meal.minerals[mineral]) || 0);
          });
        }
        if (hasAminoAcids) {
          Object.keys(meal.amino_acids).forEach(amino => {
            totals[amino] = (totals[amino] || 0) + (parseFloat(meal.amino_acids[amino]) || 0);
          });
        }
        if (hasFattyAcids) {
          Object.keys(meal.fatty_acids).forEach(fatty => {
            totals[fatty] = (totals[fatty] || 0) + (parseFloat(meal.fatty_acids[fatty]) || 0);
          });
        }

        // TIER 2: Calculate from individual foods when meal totals are empty (text/voice meals)
        if (meal.foods && Array.isArray(meal.foods)) {
          meal.foods.forEach((food: any) => {
            if (food.nutrition) {
              // Vitamins (only if meal-level vitamins not available)
              if (!hasVitamins && food.nutrition.vitamins) {
                Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                  totals[vitamin] = (totals[vitamin] || 0) + (parseFloat(food.nutrition.vitamins[vitamin]) || 0);
                });
              }
              // Minerals (only if meal-level minerals not available)
              if (!hasMinerals && food.nutrition.minerals) {
                Object.keys(food.nutrition.minerals).forEach(mineral => {
                  totals[mineral] = (totals[mineral] || 0) + (parseFloat(food.nutrition.minerals[mineral]) || 0);
                });
              }
              // Amino acids (only if meal-level amino acids not available)
              if (!hasAminoAcids && food.nutrition.amino_acids) {
                Object.keys(food.nutrition.amino_acids).forEach(amino => {
                  let value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                  // Legacy data: values < 1 are in grams (old AI prompts), convert to mg
                  if (value > 0 && value < 1) {
                    value = value * 1000;
                  }
                  totals[amino] = (totals[amino] || 0) + value;
                });
              }
              // Fatty acids (only if meal-level fatty acids not available)
              if (!hasFattyAcids && food.nutrition.fatty_acids) {
                Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                  totals[fatty] = (totals[fatty] || 0) + (parseFloat(food.nutrition.fatty_acids[fatty]) || 0);
                });
              }
            }
          });
        }
        return totals;
      }, {
        // Initialize with zero values
        calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0,
        // Vitamins
        vitamin_a: 0, vitamin_c: 0, vitamin_d: 0, vitamin_e: 0, vitamin_k: 0,
        thiamine_b1: 0, riboflavin_b2: 0, niacin_b3: 0, pantothenic_acid_b5: 0,
        pyridoxine_b6: 0, biotin_b7: 0, folate_b9: 0, vitamin_b12: 0,
        // Minerals
        calcium: 0, iron: 0, magnesium: 0, zinc: 0, potassium: 0, sodium: 0,
        phosphorus: 0, copper: 0, selenium: 0, chromium: 0, molybdenum: 0, iodine: 0,
        // Amino acids
        histidine: 0, isoleucine: 0, leucine: 0, lysine: 0, methionine: 0,
        phenylalanine: 0, threonine: 0, tryptophan: 0, valine: 0,
        // Fatty acids
        saturated: 0, monounsaturated: 0, polyunsaturated: 0, omega_3: 0, omega_6: 0
      });

      // Calculate daily averages based on actual days with meals (exclude zero days)
      const daily_averages: any = {};
      Object.keys(nutritionTotals).forEach(nutrient => {
        daily_averages[nutrient] = actualDaysWithMeals > 0 ? nutritionTotals[nutrient] / actualDaysWithMeals : 0;
      });

      return {
        meal_count: mealsExcludingToday.length,
        daily_averages,
        actual_days: actualDaysWithMeals,
        total_days: totalDaysWithData,
        excluded_days: excludedDays,
        timeframe: selectedTimeframe === 'all' ? 'all-time' : `${selectedTimeframe}d`,
        data_quality: actualDaysWithMeals >= parseInt(selectedTimeframe) * 0.7 ? 'high' :
                      actualDaysWithMeals >= parseInt(selectedTimeframe) * 0.5 ? 'medium' : 'low'
      };
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  // Fetch raw meal data for Food Source Breakdown
  const { data: rawMealData, isLoading: rawMealLoading } = useQuery({
    queryKey: coachMode ? ['client-raw-meals', clientId, selectedTimeframe] : ['raw-meals', user?.id, selectedTimeframe],
    queryFn: async () => {
      // Calculate days to fetch based on selection
      // For "Today" (0), fetch 1 day and include today
      // For other periods, add 1 to fetch extra day but exclude today
      const isToday = selectedTimeframe === '0';
      const daysToFetch = selectedTimeframe === 'all' ? 365 : parseInt(selectedTimeframe) + 1;
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?days=${daysToFetch}${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const allMeals = data.portfolio || [];

      // Filter logic based on timeframe
      const today = getTodayDateString();

      let mealsExcludingToday;
      if (isToday) {
        // "Today" - show ONLY today's meals
        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate === today;
        });
      } else if (selectedTimeframe === '1') {
        // "Yesterday" - show ONLY yesterday's meals
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const year = yesterday.getFullYear();
        const month = String(yesterday.getMonth() + 1).padStart(2, '0');
        const day = String(yesterday.getDate()).padStart(2, '0');
        const yesterdayStr = `${year}-${month}-${day}`;

        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate === yesterdayStr;
        });
      } else if (selectedTimeframe === 'all') {
        // All-time - exclude today only
        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          return mealDate !== today;
        });
      } else {
        // Other timeframes (3, 7, 14, 30 days) - filter by date range
        const daysBack = parseInt(selectedTimeframe);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - daysBack);
        const year = startDate.getFullYear();
        const month = String(startDate.getMonth() + 1).padStart(2, '0');
        const day = String(startDate.getDate()).padStart(2, '0');
        const startDateStr = `${year}-${month}-${day}`;

        mealsExcludingToday = allMeals.filter((meal: any) => {
          const mealDate = getMealDateString(meal);
          // Include meals from startDate up to (but not including) today
          return mealDate >= startDateStr && mealDate !== today;
        });
      }

      // Return raw meal data for Food Source Breakdown
      return { meals: mealsExcludingToday };
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  // Fetch user/client recommendations for personalized targets
  const { data: userRecommendations, isLoading: recsLoading } = useQuery({
    queryKey: coachMode ? ['client-recommendations', clientId] : ['user-recommendations', user?.id],
    queryFn: async () => {
      const endpoint = coachMode && clientId
        ? `/api/coach/clients/${clientId}/profile`
        : '/api/profile';

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success && data.profile) {
        // Extract and format recommendations for micronutrient components
        return {
          // Macronutrients
          calories: data.profile.daily_calorie_rec,
          protein: data.profile.protein_rec,
          carbs: data.profile.carbs_rec,
          fat: data.profile.fat_rec,
          fiber: data.profile.fiber_rec,
          // Vitamins
          vitamin_a: data.profile.vitamin_a_rec,
          vitamin_c: data.profile.vitamin_c_rec,
          vitamin_d: data.profile.vitamin_d_rec,
          vitamin_e: data.profile.vitamin_e_rec,
          vitamin_k: data.profile.vitamin_k_rec,
          thiamine_b1: data.profile.thiamin_rec,
          riboflavin_b2: data.profile.riboflavin_rec,
          niacin_b3: data.profile.niacin_rec,
          pyridoxine_b6: data.profile.vitamin_b6_rec,
          folate_b9: data.profile.folate_rec,
          vitamin_b12: data.profile.vitamin_b12_rec,
          // Minerals
          calcium: data.profile.calcium_rec,
          iron: data.profile.iron_rec,
          magnesium: data.profile.magnesium_rec,
          phosphorus: data.profile.phosphorus_rec,
          potassium: data.profile.potassium_rec,
          sodium: data.profile.sodium_rec,
          zinc: data.profile.zinc_rec,
          selenium: data.profile.selenium_rec,
          // Essential Fatty Acids
          omega_3: data.profile.omega3_rec,
          omega_6: data.profile.omega6_rec,
          // Amino Acids
          lysine: data.profile.lysine_rec,
          leucine: data.profile.leucine_rec,
          isoleucine: data.profile.isoleucine_rec,
          phenylalanine: data.profile.phenylalanine_rec,
          methionine: data.profile.methionine_rec,
          valine: data.profile.valine_rec,
          threonine: data.profile.threonine_rec,
          tryptophan: data.profile.tryptophan_rec,
          histidine: data.profile.histidine_rec
        };
      }
      return null;
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  // Fetch all-time micronutrient data - use direct API for consistency
  const { data: allTimeMicronutrientData, isLoading: allTimeLoading } = useQuery({
    queryKey: coachMode ? ['client-all-time-micronutrients', clientId] : ['all-time-micronutrients', user?.id],
    queryFn: async () => {
      // Get all available data (365 days max)
      const url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002'}/api/meals/portfolio?days=365${coachMode && clientId ? `&client_id=${clientId}` : ''}`;
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      const data = await response.json();
      const allMeals = data.portfolio || [];
      
      // Process all-time micronutrient data with proper day-based averaging
      // Group meals by date to get actual days with data
      const mealsByDate = new Map<string, any[]>();
      allMeals.forEach((meal: any) => {
        const mealDate = getMealDateString(meal);
        if (!mealsByDate.has(mealDate)) {
          mealsByDate.set(mealDate, []);
        }
        mealsByDate.get(mealDate)!.push(meal);
      });
      
      const actualDaysWithMeals = mealsByDate.size;
      console.log(`🔍 All-time analysis: ${allMeals.length} meals across ${actualDaysWithMeals} days`);

      // Debug: Check meal structure for troubleshooting
      if (allMeals.length > 0) {
        console.log('📋 Sample meal structure:', {
          hasTotals: !!allMeals[0].totals,
          hasNutritionTotals: !!allMeals[0].nutrition_totals,
          hasFoods: !!allMeals[0].foods,
          sampleMeal: allMeals[0]
        });
      }

      // Calculate total nutrition across all meals
      const nutritionTotals = allMeals.reduce((totals: any, meal: any) => {
        // Process macronutrients - try multiple sources with fallback
        const macroSource = meal.totals || meal.nutrition_totals || {};

        // CRITICAL: Parse to number to prevent string concatenation!
        totals.calories += parseFloat(macroSource.calories) || 0;
        totals.protein += parseFloat(macroSource.protein) || 0;
        totals.carbs += parseFloat(macroSource.carbs) || 0;
        totals.fat += parseFloat(macroSource.fat) || 0;
        totals.fiber += parseFloat(macroSource.fiber) || 0;

        // If no macros from totals, try summing from foods
        if (!macroSource.calories && meal.foods && Array.isArray(meal.foods)) {
          meal.foods.forEach((food: any) => {
            if (food.nutrition) {
              totals.calories += parseFloat(food.nutrition.calories) || 0;
              totals.protein += parseFloat(food.nutrition.protein) || 0;
              totals.carbs += parseFloat(food.nutrition.carbs || food.nutrition.carbohydrates) || 0;
              totals.fat += parseFloat(food.nutrition.fat) || 0;
              totals.fiber += parseFloat(food.nutrition.fiber) || 0;
            }
          });
        }

        // TIER 1: Use meal-level micronutrient totals if available (photo meals)
        const hasVitamins = meal.vitamins && Object.keys(meal.vitamins).length > 0;
        const hasMinerals = meal.minerals && Object.keys(meal.minerals).length > 0;
        const hasAminoAcids = meal.amino_acids && Object.keys(meal.amino_acids).length > 0;
        const hasFattyAcids = meal.fatty_acids && Object.keys(meal.fatty_acids).length > 0;

        if (hasVitamins) {
          Object.keys(meal.vitamins).forEach(vitamin => {
            totals[vitamin] = (totals[vitamin] || 0) + (parseFloat(meal.vitamins[vitamin]) || 0);
          });
        }
        if (hasMinerals) {
          Object.keys(meal.minerals).forEach(mineral => {
            totals[mineral] = (totals[mineral] || 0) + (parseFloat(meal.minerals[mineral]) || 0);
          });
        }
        if (hasAminoAcids) {
          Object.keys(meal.amino_acids).forEach(amino => {
            totals[amino] = (totals[amino] || 0) + (parseFloat(meal.amino_acids[amino]) || 0);
          });
        }
        if (hasFattyAcids) {
          Object.keys(meal.fatty_acids).forEach(fatty => {
            totals[fatty] = (totals[fatty] || 0) + (parseFloat(meal.fatty_acids[fatty]) || 0);
          });
        }

        // TIER 2: Calculate from individual foods when meal totals are empty (text/voice meals)
        if (meal.foods && Array.isArray(meal.foods)) {
          meal.foods.forEach((food: any) => {
            if (food.nutrition) {
              // Vitamins (only if meal-level vitamins not available)
              if (!hasVitamins && food.nutrition.vitamins) {
                Object.keys(food.nutrition.vitamins).forEach(vitamin => {
                  totals[vitamin] = (totals[vitamin] || 0) + (parseFloat(food.nutrition.vitamins[vitamin]) || 0);
                });
              }
              // Minerals (only if meal-level minerals not available)
              if (!hasMinerals && food.nutrition.minerals) {
                Object.keys(food.nutrition.minerals).forEach(mineral => {
                  totals[mineral] = (totals[mineral] || 0) + (parseFloat(food.nutrition.minerals[mineral]) || 0);
                });
              }
              // Amino acids (only if meal-level amino acids not available)
              if (!hasAminoAcids && food.nutrition.amino_acids) {
                Object.keys(food.nutrition.amino_acids).forEach(amino => {
                  let value = parseFloat(food.nutrition.amino_acids[amino]) || 0;
                  // Legacy data: values < 1 are in grams (old AI prompts), convert to mg
                  if (value > 0 && value < 1) {
                    value = value * 1000;
                  }
                  totals[amino] = (totals[amino] || 0) + value;
                });
              }
              // Fatty acids (only if meal-level fatty acids not available)
              if (!hasFattyAcids && food.nutrition.fatty_acids) {
                Object.keys(food.nutrition.fatty_acids).forEach(fatty => {
                  totals[fatty] = (totals[fatty] || 0) + (parseFloat(food.nutrition.fatty_acids[fatty]) || 0);
                });
              }
            }
          });
        }
        return totals;
      }, {
        // Initialize with zero values
        calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0,
        // Vitamins
        vitamin_a: 0, vitamin_c: 0, vitamin_d: 0, vitamin_e: 0, vitamin_k: 0,
        thiamine_b1: 0, riboflavin_b2: 0, niacin_b3: 0, pantothenic_acid_b5: 0,
        pyridoxine_b6: 0, biotin_b7: 0, folate_b9: 0, vitamin_b12: 0,
        // Minerals
        calcium: 0, iron: 0, magnesium: 0, zinc: 0, potassium: 0, sodium: 0,
        phosphorus: 0, copper: 0, selenium: 0, chromium: 0, molybdenum: 0, iodine: 0,
        // Amino acids
        histidine: 0, isoleucine: 0, leucine: 0, lysine: 0, methionine: 0,
        phenylalanine: 0, threonine: 0, tryptophan: 0, valine: 0,
        // Fatty acids
        saturated: 0, monounsaturated: 0, polyunsaturated: 0, omega_3: 0, omega_6: 0
      });
      
      // Calculate daily averages based on actual days with meals
      const daily_averages: any = {};
      Object.keys(nutritionTotals).forEach(nutrient => {
        daily_averages[nutrient] = actualDaysWithMeals > 0 ? nutritionTotals[nutrient] / actualDaysWithMeals : 0;
      });

      // Debug: Log the macronutrient totals and averages
      console.log('🔢 All-time macro totals:', {
        calories: nutritionTotals.calories,
        protein: nutritionTotals.protein,
        carbs: nutritionTotals.carbs,
        fat: nutritionTotals.fat,
        fiber: nutritionTotals.fiber
      });
      console.log('📊 All-time daily averages:', {
        calories: daily_averages.calories,
        protein: daily_averages.protein,
        carbs: daily_averages.carbs,
        fat: daily_averages.fat,
        fiber: daily_averages.fiber,
        days: actualDaysWithMeals
      });

      return {
        meal_count: allMeals.length,
        daily_averages,
        actual_days: actualDaysWithMeals,
        timeframe: 'all-time'
      };
    },
    enabled: coachMode ? !!clientId : !!user?.id,
  });

  const reports = [
    {
      id: 'micronutrients',
      title: 'Micronutrient Analysis (Dynamic)',
      description: '44-nutrient analysis with RDA comparisons - use timeframe selector above',
      icon: '🧬',
      component: <MicronutrientMatrix micronutrientData={micronutrientData} userRecommendations={userRecommendations} />
    },
    {
      id: 'all-time-micronutrients',
      title: 'All-Time Micronutrient Matrix',
      description: '44-nutrient analysis across all logged meals (excluding zero days)',
      icon: '🌟',
      component: <AllTimeMicronutrientMatrix micronutrientData={allTimeMicronutrientData} userRecommendations={userRecommendations} />
    },
    {
      id: 'trends',
      title: 'Nutrient Trends Over Time',
      description: 'Track nutrient changes week-over-week',
      icon: '📈',
      component: <NutrientTrendsChart user={user} clientMode={coachMode} />
    },
    {
      id: 'deficiencies',
      title: 'Deficiency Alert Report',
      description: 'Identify nutrients below recommended levels',
      icon: '⚠️',
      component: <div className="card"><div className="card-body"><p className="text-gray-500">Coming Soon: Deficiency alerts</p></div></div>
    },
    {
      id: 'food-sources',
      title: 'Food Source Breakdown',
      description: 'See which foods contribute which nutrients',
      icon: '🍎',
      component: <FoodSourceBreakdown micronutrientData={rawMealData} />
    },
    {
      id: 'compliance',
      title: 'RDA Compliance Chart',
      description: 'Visual overview of RDA achievement rates',
      icon: '✅',
      component: <div className="card"><div className="card-body"><p className="text-gray-500">Coming Soon: RDA compliance visualization</p></div></div>
    }
  ];

  if (micronutrientLoading || allTimeLoading || rawMealLoading || recsLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-200 rounded-lg h-32 animate-pulse" />
        <div className="bg-gray-200 rounded-lg h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-sm">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-white">
                Nutrition Overview 📊
              </h1>
              <p className="mt-2 text-green-100">
                Comprehensive nutrition analysis and reporting for {user?.fullName || user?.email}
              </p>
            </div>

            {/* Timeframe Selector */}
            <div className="flex flex-col items-end gap-2">
              <label className="text-sm text-green-100 font-medium">Analysis Period:</label>
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value)}
                className="px-4 py-2 bg-white text-gray-900 rounded-md shadow-sm border border-gray-300 focus:ring-2 focus:ring-green-400 focus:border-transparent"
              >
                <option value="0">Today</option>
                <option value="1">Yesterday</option>
                <option value="3">Last 3 days</option>
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
                <option value="all">All-Time</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Data Quality Indicator */}
      {selectedReport === 'micronutrients' && micronutrientData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Data Quality Analysis</h3>
              <p className="text-xs text-gray-500 mt-1">
                {micronutrientData.actual_days} quality days out of {micronutrientData.total_days} days with data
                {micronutrientData.excluded_days > 0 && (
                  <span className="ml-1">
                    ({micronutrientData.excluded_days} incomplete {micronutrientData.excluded_days === 1 ? 'day' : 'days'} excluded)
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                micronutrientData.data_quality === 'high' ? 'bg-green-100 text-green-800' :
                micronutrientData.data_quality === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {micronutrientData.data_quality === 'high' ? '⭐⭐⭐ High Confidence' :
                 micronutrientData.data_quality === 'medium' ? '⭐⭐ Medium Confidence' :
                 '⭐ Low Confidence'}
              </span>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-600">
            <strong>Note:</strong> Days with &lt;2 meals and &lt;800 calories are excluded to ensure accurate averages.
          </div>
        </div>
      )}

      {/* Report Portfolio */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Report Selection Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Available Reports</h3>
            </div>
            <div className="card-body p-0">
              <nav className="space-y-1">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    onClick={() => setSelectedReport(report.id)}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                      selectedReport === report.id
                        ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-500'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-lg mr-3">{report.icon}</span>
                      <div>
                        <div className="font-medium">{report.title}</div>
                        <div className="text-xs text-gray-500">{report.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Report Display Area */}
        <div className="lg:col-span-3">
          {reports.find(r => r.id === selectedReport)?.component}
        </div>
      </div>
    </div>
  );
};